/* The fourteen trades — the canonical list (TRADES, below) and the glyphs.
 *
 * The marketing page shows six of these as `.category-chip` glyphs
 * (shoku-web/public/index.html, the categories section) while the homeowner's
 * post-a-job form shipped twelve full-colour EMOJI — the only emoji in the
 * product, rendered differently by Apple, Google and Microsoft, sitting on a
 * palette that is otherwise monochrome plus one orange.
 *
 * The six that already existed are lifted verbatim; the other six are drawn to
 * the same spec (24x24, 1.8 stroke, round caps and joins, no fills) so a chip
 * row cannot be told apart from the marketing site's. They live here rather
 * than in either site because both product sites pick a trade: the homeowner
 * chooses one when posting, the contractor lists several on their profile.
 */

import type { ReactElement, ReactNode, SVGProps } from "react";

import { isSafeGlyphBody } from "./glyphSafety";

/** Re-exported so `@ui/react` is the single import for both the glyph and the
 *  check that lets a remote one render — and so the two cannot drift apart. */
export { isSafeGlyphBody } from "./glyphSafety";

type TradeGlyphProps = SVGProps<SVGSVGElement>;
type TradeGlyph = (props: TradeGlyphProps) => ReactElement;

/* Either drawn children or a re-validated remote body — never NEITHER, and
 * never both. React itself refuses both at once, and the union refuses
 * neither, so a glyph component written as `<Glyph {...p} />` with nothing
 * inside it fails the build instead of shipping an empty 24x24 <svg>. That is
 * the same class of check as BY_TRADE's `satisfies`, which only proves a glyph
 * component EXISTS, not that it draws anything. */
type GlyphContent = { children: ReactNode } | { dangerouslySetInnerHTML: { __html: string } };

function Glyph({ children, ...props }: TradeGlyphProps & GlyphContent) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      // 1.8, matching index.html's category glyphs exactly — icons.tsx's
      // interface icons are 1.75 and these two sets sit side by side nowhere.
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      width="1.25em"
      height="1.25em"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/* ---- The six from the marketing page, unchanged ------------------------- */

export const PlumbingIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M14.7 6.3a4 4 0 0 0-5.6 5.1L4 16.5V20h3.5l5-5.1a4 4 0 0 0 5.1-5.6l-2.6 2.6-2-2 2.7-2.6Z" />
  </Glyph>
);

export const ElectricalIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z" />
  </Glyph>
);

export const PaintingIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <rect x="4" y="4" width="12" height="6" rx="1.5" />
    <path d="M8 10v4" />
    <rect x="6.5" y="14" width="3" height="6" rx="1" />
  </Glyph>
);

export const CarpentryIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="m14 6 4 4-2.5 2.5L11 8Z" />
    <path d="M11 8 5 14l-1.5 4L8 16l6-6" />
  </Glyph>
);

export const LandscapingIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M20 4C10 4 4 10 4 18c8 0 14-6 14-14Z" />
    <path d="M4 20 12 12" />
  </Glyph>
);

export const ApplianceIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M9 3v6M15 3v6" />
    <path d="M6 9h12v3a6 6 0 0 1-12 0V9Z" />
    <path d="M12 18v3" />
  </Glyph>
);

/* ---- The six the marketing page never needed ---------------------------- */

export const RoofingIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M3 12 12 4.5 21 12" />
    <path d="M5.8 10v9.5h12.4V10" />
    <path d="M10 19.5V15h4v4.5" />
  </Glyph>
);

export const HvacIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9" />
    <path d="m9.5 5 2.5 2.5L14.5 5M9.5 19l2.5-2.5 2.5 2.5" />
  </Glyph>
);

export const CleaningIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M10 3h4v3h-4z" />
    <path d="M14 4.5h3v2" />
    <path d="M9 6h6a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" />
    <path d="M9.5 11h5" />
  </Glyph>
);

export const FlooringIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9.3h18M3 14.7h18" />
    <path d="M9 4v5.3M15 9.3v5.4M9 14.7V20" />
  </Glyph>
);

export const PestIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M9.5 5.5 8 3.5M14.5 5.5 16 3.5" />
    <path d="M12 6a5 5 0 0 1 5 5v3a5 5 0 0 1-10 0v-3a5 5 0 0 1 5-5Z" />
    <path d="M7 10.5H3.5M17 10.5h3.5M7 14.5H4M17 14.5h3" />
  </Glyph>
);

export const GeneralIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M4 9h16a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1Z" />
    <path d="M9 9V6.5A1.5 1.5 0 0 1 10.5 5h3A1.5 1.5 0 0 1 15 6.5V9" />
    <path d="M3 13.5h18" />
  </Glyph>
);

export const DogGroomingIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M6.5 16a5.5 4.5 0 1 0 11 0a5.5 4.5 0 1 0 -11 0M4.2 9a1.8 1.8 0 1 0 3.6 0a1.8 1.8 0 1 0 -3.6 0M8.2 5.5a1.8 1.8 0 1 0 3.6 0a1.8 1.8 0 1 0 -3.6 0M12.2 5.5a1.8 1.8 0 1 0 3.6 0a1.8 1.8 0 1 0 -3.6 0M16.2 9a1.8 1.8 0 1 0 3.6 0a1.8 1.8 0 1 0 -3.6 0" />
  </Glyph>
);

/* A whole dog mid-stride, not a paw — the paw is Dog Groomer's. No leash: at
 * chip size (20px) a lead and its handle close up into a blot over the head. */
export const DogWalkingIcon = (p: TradeGlyphProps) => (
  <Glyph {...p}>
    <path d="M3.5 7.5Q4 10.5 6.5 11H14.2L15 7.2Q15.5 5 17.5 5Q19 5 19.3 6.5L21 7.4Q21.8 7.8 21.4 8.8Q21 9.5 20 9.5H18.6L17.4 11.5V15H8Q6.5 15 6.5 13.5V11" />
    <path d="M17 5.3Q15.8 6.8 16.3 9" />
    <path d="M8.5 15 7.6 20M16 15l1 5" />
  </Glyph>
);

/**
 * THE trade list for every Shoku web surface.
 *
 * `value` is the wire contract: the string the Go API stores in `job.category`
 * and in a contractor's `skills[]`. GetNearbyContractors compares the two with
 * `s == skill` (shared/pkg/firestore/operations.go) — exact, case-sensitive, no
 * trimming — so a one-byte drift returns 200 with an empty list forever rather
 * than an error. `label` is a display decision that may change ("Appliance
 * Repair" here, "Appliances" on a phone); never send a label to the API.
 *
 * Both product sites re-export this instead of declaring their own: the
 * homeowner site as JOB_CATEGORIES (src/types/dto.ts), the contractor site as
 * JOB_CATEGORIES + JobCategory (src/lib/types/enums.ts). It lives beside the
 * glyphs because BY_TRADE's `satisfies Record<Trade, …>` is what turns adding a
 * trade without drawing its icon into a compile error instead of a second
 * toolbox in the chip row. Same reason the status WORDING lives in statusTone.
 *
 * NOT shared with the two Flutter apps — they cannot import TypeScript and keep
 * their own copies in `lib/features/shared/domain/enums.dart`, which must be
 * edited to match. That, and the marketing page's hand-written subset in
 * shoku-web/public/index.html, are the remaining hand-kept copies.
 *
 * Order is the on-screen order of every chip row, so APPEND — three Maestro
 * flows tap "Plumbing" on a horizontal rail Maestro cannot scroll on iOS
 * (user-app/08, user-app/06, contractor-app/04), and `general` reads as the
 * catch-all only while it is last.
 */
export const TRADES = [
  { value: "plumbing", label: "Plumber" },
  { value: "electrical", label: "Electrician" },
  { value: "carpentry", label: "Carpentry" },
  { value: "painting", label: "Painting" },
  { value: "roofing", label: "Roofing" },
  { value: "hvac", label: "HVAC" },
  { value: "landscaping", label: "Gardener" },
  { value: "cleaning", label: "Specialist Cleaning" },
  { value: "appliance", label: "Appliance Repair" },
  { value: "flooring", label: "Flooring" },
  { value: "pest", label: "Pest Control" },
  { value: "dog_grooming", label: "Dog Groomer" },
  { value: "dog_walking", label: "Dog Walker" },
  { value: "general", label: "General Maintenance" },
] as const;

/** A trade's wire value — the half of TRADES that is a contract, not a caption. */
export type Trade = (typeof TRADES)[number]["value"];

/**
 * The trades new work can be filed under at launch — a subset of TRADES's wire
 * values, not a separate list: every TRADES value stays parseable and
 * renderable (`general` included), this only narrows what the PICKERS offer.
 * Phase 2 replaces this with `GET /categories`.
 */
export const LAUNCH_TRADES: readonly Trade[] = ["plumbing", "electrical", "landscaping", "dog_grooming"];

/** The label for a raw wire value, falling back to the value itself so a trade
 *  the backend adds tomorrow reads as "solar" rather than as an empty chip. */
export function tradeLabel(value: string): string {
  return TRADES.find((trade) => trade.value === value)?.label ?? value;
}

/**
 * Keyed by the raw wire value, NOT by a label. Annotated loosely so `TradeIcon`
 * can still be handed any string, but checked against `Trade` by the `satisfies`
 * below: a trade added to TRADES without a glyph here fails the build.
 */
const BY_TRADE: Record<string, TradeGlyph | undefined> = {
  plumbing: PlumbingIcon,
  electrical: ElectricalIcon,
  carpentry: CarpentryIcon,
  painting: PaintingIcon,
  roofing: RoofingIcon,
  hvac: HvacIcon,
  landscaping: LandscapingIcon,
  cleaning: CleaningIcon,
  appliance: ApplianceIcon,
  flooring: FlooringIcon,
  pest: PestIcon,
  dog_grooming: DogGroomingIcon,
  dog_walking: DogWalkingIcon,
  general: GeneralIcon,
} satisfies Record<Trade, TradeGlyph>;

/**
 * The glyph for a trade: the BUNDLED drawing, else a remote `pathBody` that
 * survives re-validation, else the toolbox.
 *
 * ⚠️ BUNDLED WINS — and that is deliberately the OPPOSITE of how a LABEL
 * resolves. A remote label beats the bundled one (lib/trades.ts on the
 * homeowner site, SkillsPicker on the contractor site) because relabelling a
 * trade without a deploy is the entire point of config/trades. A remote GLYPH
 * only fills a HOLE: the goal is that a genuinely NEW trade arrives with its
 * own artwork, never that an operator can redraw Plumbing. Bundled-first means
 * a bad paste — or a tampered document — cannot break a glyph that already
 * ships, and it costs nothing, because a trade every client bundles never
 * needs a remote icon (`shoku-admin trades help` says exactly that).
 *
 * `pathBody` BYPASSES BY_TRADE rather than widening `Trade`: the `satisfies
 * Record<Trade, TradeGlyph>` above still fails the build when a bundled trade
 * is added without a drawing, which is the check that stops the chip row
 * quietly growing a second toolbox.
 *
 * The body is re-validated HERE, at the render — not only by the CLI that
 * wrote it, which is not the only thing that can write it. See glyphSafety.ts.
 * An unsafe body is REFUSED (toolbox), never cleaned up.
 *
 * A trade with no bundled glyph and no icon still renders as "general" rather
 * than as a hole in the chip row — the label beside it says what it is, and an
 * icon is never the only thing carrying the meaning.
 */
export function TradeIcon({
  trade,
  pathBody,
  ...props
}: TradeGlyphProps & { trade: string; pathBody?: string | undefined }) {
  const Component = BY_TRADE[trade];
  if (Component) return <Component {...props} />;
  if (pathBody && isSafeGlyphBody(pathBody)) {
    return <Glyph {...props} dangerouslySetInnerHTML={{ __html: pathBody }} />;
  }
  return <GeneralIcon {...props} />;
}
